import { useCallback } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Text, Icon, Calendar, iconWeight } from '@nula/core-ui';
import { useCalendarStore } from '@/stores/use-calendar-store';
import type { CalendarEvent } from '@/stores/use-calendar-store';
import { getCalendarColor } from '@/lib/calendar/calendar-colors';
import styles from './calendar-month-grid.module.css';

const eventCard = cva(styles.eventCard);

interface CalendarMonthGridProps {
  year: number;
  month: number;
  events: Record<string, CalendarEvent[]>;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  onSelectEvent: (event: CalendarEvent) => void;
  showWeekends?: boolean;
}

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmt12(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export function CalendarMonthGrid({
  year, month, events, selectedDate, onSelectDate, onSelectEvent, showWeekends = true,
}: CalendarMonthGridProps) {
  const { subscriptions } = useCalendarStore();
  const value = selectedDate ?? undefined;

  const handleChange = useCallback((dateStr: string) => {
    onSelectDate(dateStr);
  }, [onSelectDate]);

  const dateCellRender = useCallback((date: Date) => {
    const key = toDateKey(date);
    const dayEvents = events[key] ?? [];
    if (dayEvents.length === 0) return null;

    return (
      <>
        {dayEvents.slice(0, 3).map(event => {
          const isBusy = event.title === 'Busy';
          const startTime = new Date(event.startAt);
          const timeLabel = event.isAllDay ? 'All day' : fmt12(startTime);
          const calColor = getCalendarColor(event.subscriptionId, subscriptions[event.subscriptionId]?.color);
          const eventColorVar = isBusy
            ? { '--event-color': 'var(--on-surface-variant)' } as React.CSSProperties
            : { '--event-color': calColor } as React.CSSProperties;

          return (
            <Flex
              key={event.id}
              vertical
              onClick={e => { e.stopPropagation(); onSelectEvent(event); }}
              className={eventCard()}
              style={eventColorVar}
            >
              {/* Title row */}
              <Flex align="center" gap="xs">
                <Icon
                  name={isBusy ? 'block' : 'schedule'}
                  size={10}
                  weight={iconWeight.light}
                  className={styles.eventIcon}
                />
                <Text
                  size="xs"
                  className={clsx(
                    styles.eventTitle,
                    isBusy ? styles.eventTitleBusy : styles.eventTitleDefault,
                  )}
                >
                  {event.title}
                </Text>
              </Flex>

              {/* Time label */}
              <Text className={styles.eventTime}>
                {timeLabel}
              </Text>
            </Flex>
          );
        })}
        {dayEvents.length > 3 && (
          <Text
            className={styles.moreLabel}
            onClick={e => { e.stopPropagation(); onSelectDate(key); }}
          >
            +{dayEvents.length - 3} more
          </Text>
        )}
      </>
    );
  }, [events, onSelectDate, onSelectEvent, subscriptions]);

  return (
    <Calendar
      variant="page"
      value={value}
      defaultValue={`${year}-${String(month + 1).padStart(2, '0')}-01`}
      onChange={handleChange}
      dateCellRender={dateCellRender}
      showWeekends={showWeekends}
    />
  );
}
